import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ViewListIcon from "@mui/icons-material/ViewList";

import { setSnackbar } from "../../../actions/snackbarActions";
import { appCtx } from "../../../utils/hooks/appContext";
import { getInstallationType, getUnitTypeLabel } from "../../../utils/helper";
import {
  deleteProjectItem,
  getProjectItemsByAdmin,
  getProjectsItem,
} from "../../../utils/services/projectService";
import { sizeOfUnitMapping, unitTypeReverseMapping } from "../../../utils/unitConstants";

function ProjectItemsTable(props) {
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [open, setOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { projectId } = useContext(appCtx);
  const { isClivet } = useSelector((state) => state.brand);

  const sizeOfUnitValueFormatter = ({ value }) =>
    sizeOfUnitMapping[value] || value;

  const setTableRows = (projectsItem) => {
    setRows(
      projectsItem.map((projectItem) => ({
        id: projectItem.id,
        projectId: props.projectId,
        name: projectItem.name,
        projectNumber: props.projectNumber,
        ident: props.projectIdent,
        projectName: props.projectName,
        department: props.projectDepartment,
        project: projectItem.project,
        price: projectItem.price,
        revisionCount: projectItem.revisionCount,
        revisionNumber: projectItem.revisionNumber,
        area: projectItem.area,
        height: projectItem.height,
        buildingType: projectItem.buildingType,
        changeRate: projectItem.changeRate,
        dropSupply: projectItem.dropSupply,
        flowSupply: projectItem.flowSupply,
        flowReturn: projectItem.flowReturn,
        dropReturn: projectItem.dropReturn,
        efficiencyClass: projectItem.EfficiencyClass,
        maintenance: projectItem.maintenance,
        casingQuality: projectItem.casingQuality,
        installation: projectItem.installation,
        unitType: projectItem.unitType,
        sizeOfUnit: projectItem.sizeOfUnit,
        sizeOfUnitLabel:
          sizeOfUnitMapping[projectItem.sizeOfUnit] || "Missing value",
        heatRecovery: projectItem.heatRecovery,
        airflowDirection: projectItem.AirflowDirection,
        controls: projectItem.controls,
        summerOutdoorTemperature: projectItem.summerOutdoorTemperature,
        summerWetBulbTemperature: projectItem.summerWetBulbTemperature,
        summerDewPointTemperature: projectItem.summerDewPointTemperature,
        summerOutdoorHumidity: projectItem.summerOutdoorHumidity,
        summerIndoorTemperature: projectItem.summerIndoorTemperature,
        summerIndoorHumidity: projectItem.summerIndoorHumidity,
        winterOutdoorTemperature: projectItem.winterOutdoorTemperature,
        winterOutdoorHumidity: projectItem.winterOutdoorHumidity,
        winterIndoorTemperature: projectItem.winterIndoorTemperature,
        winterIndoorHumidity: projectItem.winterIndoorHumidity,
        supplyAirFilter: projectItem.supplyAirFilter,
        doubleStageFiltering: projectItem.doubleStageFiltering,
        extractAirFilter: projectItem.extractAirFilter,
        filteringAdvancedSettings: projectItem.filteringAdvancedSettings,
        differentialPressureIndicator:
          projectItem.differentialPressureIndicator,
        fineFilterType: projectItem.fineFilterType,
        fineFilterClassSupply: projectItem.fineFilterClassSupply,
        fineFilterClassReturn: projectItem.fineFilterClassReturn,
        fanAdvancedSettings: projectItem.fanAdvancedSettings,
        fanType: projectItem.fanType,
        fanEfficiencyClass: projectItem.fanEfficiencyClass,
      })),
    );
  };

  useEffect(() => {
    const pId = props.projectId ?? projectId;
    const fetchItems = props.admin
      ? getProjectItemsByAdmin(pId)
      : getProjectsItem(pId);

    fetchItems
      .then((response) => {
        if (response.statusCode >= 200 && response.statusCode < 300) {
          setTableRows(response.data);
        }
      })
      .catch(() => {
        dispatch(setSnackbar(true, "error", t("snackbar.fetchingDataError")));
      });
  }, [props.rowsChanged]);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const editProjectItem = (params) => {

    const ahuType =
      unitTypeReverseMapping[params.row.unitType] ?? 1;
    const targetProjectId = props.projectId ?? projectId ?? 0;

    navigate(
      `/create-project/${targetProjectId}/configurator/${ahuType}?isEditing=true&projectItemId=${params.row.id}`,
    );
  };

  const openRevisions = (id) => {
    window.scrollTo(0, 0);
    navigate("/project-item-revision", {
      state: { id: id.row.id, name: props.projectName, admin: props.admin },
    });
  };

  const deleteProj = () => {
    deleteProjectItem(selectionModel[0])
      .then((response) => {
        if (response.statusCode >= 200 && response.statusCode < 300) {
          dispatch(
            setSnackbar(
              true,
              "success",
              t("snackbar.deleteProjectItemSuccess"),
            ),
          );
          props.setRowsChanged((prev) => !prev);
        } else {
          dispatch(
            setSnackbar(true, "error", t("snackbar.deleteProjectItemError")),
          );
        }
      })
      .catch(() => {
        dispatch(
          setSnackbar(true, "error", t("snackbar.deleteProjectItemError")),
        );
      });

    handleClose();
  };

  const RenderButtonEdit = (id) => (
    <Button onClick={() => editProjectItem(id)}>
      <EditIcon sx={{ color: theme.palette.primary.main }} />
    </Button>
  );

  const RenderButtonOpen = (id) => (
    <Button onClick={() => openRevisions(id)}>
      <FolderOpenIcon sx={{ color: theme.palette.primary.main }} />
    </Button>
  );

  const RenderButtonDelete = () => (
    <Box>
      <Button onClick={handleClickOpen}>
        <DeleteIcon sx={{ color: theme.palette.primary.main }} />
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: { padding: "10px 20px" },
        }}
        disableScrollLock={true}
        sx={{ backgroundColor: theme.palette.primary.light }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ fontSize: "30px" }}>
          {t("projectitem.dialogTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ fontSize: "20px", color: "#222" }}
          >
            {t("projectitem.dialogContentText")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              fontSize: "18px",
              marginRight: "5px",
              padding: "2px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                color: theme.palette.secondary.main,
              },
            }}
          >
            {t("projectitem.buttonNo")}
          </Button>
          <Button
            onClick={deleteProj}
            variant="outlined"
            sx={{
              fontSize: "18px",
              marginRight: "5px",
              padding: "2px",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.9)",
                color: theme.palette.secondary.main,
              },
            }}
          >
            {t("projectitem.buttonYes")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  const columns = [
    {
      field: "btnOpen",
      headerName: t("projectTableheaderName.open"),
      minWidth: 70,
      renderCell: RenderButtonOpen,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "header-style",
    },
    {
      field: "id",
      headerName: "ID",
      minWidth: 70,
      align: "center",
      headerClassName: "header-style",
      headerAlign: "center",
    },
    {
      field: "name",
      headerName: t("projectitem.fieldName"),
      minWidth: 210,
      headerAlign: "center",
      headerClassName: "header-style",
      align: "center",
    },
    {
      field: "price",
      headerName: t("projectitem.price"),
      minWidth: 100,
      headerAlign: "center",
      type: "number",
      valueFormatter: ({ value }) => `${value.toFixed(2)} €`,
      align: "center",
      headerClassName: "header-style",
    },
    {
      field: "revisionCount",
      headerName: t("projectitem.revisionCount"),
      minWidth: 160,
      headerAlign: "center",
      headerClassName: "header-style",
      align: "center",
    },
    {
      field: "flowSupply",
      headerName: t("projectItemTable.flowSupply"),
      type: "number",
      minWidth: 220,
      align: "center",
      headerClassName: "header-style",
      headerAlign: "center",
    },
    {
      field: "flowReturn",
      headerName: t("projectItemTable.flowReturn"),
      type: "number",
      minWidth: 230,
      align: "center",
      headerClassName: "header-style",
      headerAlign: "center",
    },
    {
      field: "casingQuality",
      headerName: t("projectItemTable.casingQuality"),
      minWidth: 200,
      headerClassName: "header-style",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "installation",
      headerName: t("projectItemTable.installation"),
      minWidth: 200,
      headerClassName: "header-style",
      valueFormatter: ({ value }) => t(`unitTypeList.${getInstallationType(value)}`),
      headerAlign: "center",
      align: "center",
    },
    {
      field: "unitType",
      headerName: t("projectItemTable.unitType"),
      minWidth: 150,
      headerClassName: "header-style",
      valueFormatter: ({ value }) => getUnitTypeLabel(value, isClivet),
      headerAlign: "center",
      align: "center",
    },
    {
      field: "sizeOfUnit",
      valueFormatter: sizeOfUnitValueFormatter,
      headerName: t("projectItemTable.sizeOfUnit"),
      minWidth: 170,
      headerClassName: "header-style",
      headerAlign: "center",
      align: "center",
    },

    {
      field: "heatRecovery",
      headerName: t("projectItemTable.heatRecovery"),
      minWidth: 220,
      headerClassName: "header-style",
      valueFormatter: ({ value }) => t(`unitTypeList.${value}`),
      headerAlign: "center",
      align: "center",
    },

    {
      field: "btnEdit",
      headerName: t("projectItemTable.edit"),
      minWidth: 130,
      renderCell: RenderButtonEdit,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "header-style",
    },
    {
      field: "btnDelete",
      headerName: t("projectItemTable.delete"),
      minWidth: 70,
      renderCell: RenderButtonDelete,
      align: "center",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      headerClassName: "header-style",
    },
  ];

  return (
    rows.length > 0 && (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <ViewListIcon sx={{ width: 50, height: 50, m: 1 }} />
          <Typography
            component="h1"
            variant="h4"
            sx={{ margin: 2, fontSize: "30px" }}
          >
            {t("projectitem.tabname")} COMPACT | pro
          </Typography>
        </Box>
        <DataGrid
          rows={rows}
          columns={columns}
          // autoPageSize
          autoHeight
          rowsPerPageOptions={[10, 20, 100]}
          sx={{
            // " min-height": "60%",
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.secondary.main,
            },
            ".MuiTablePagination-select": {
              "margin-top": "-3px",
            },
            ".MuiTablePagination-selectIcon": {
              "margin-top": "-3px",
            },
            ".MuiDataGrid-columnHeaderTitle.css-1jbbcbn-MuiDataGrid-columnHeaderTitle":
              {
                fontWeight: "bold",
                paddingLeft: "5px",
              },
          }}
          components={{
            NoRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {t("projectitemrevision.noRows")}
              </Stack>
            ),
          }}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: t("tableRows.rowsPerPage"),
              labelDisplayedRows: ({ from, to, count }) =>
                `${from} - ${to} ${t("tableRows.of")} ${count}`,
            },
          }}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          selectionModel={selectionModel}
          {...rows}
        />
      </Box>
    )
  );
}

export default ProjectItemsTable;
